import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Divider,
} from "@mui/material";

import { useAppTranslation } from "../../hooks/useAppTranslation";
import { PAYMENT_STATUS_CONFIG } from "../../constants/paymentStatus";

type Props = {
  open: boolean;
  loading?: boolean;

  selectedCount: number;

  initialValue?: string | null;

  /** BONUS: aktualne statusy zaznaczonych dokumentów */
  currentStatuses?: Record<string, number>;

  onClose: () => void;
  onConfirm: (paymentStatus: string) => void;
};

export default function BulkPaymentStatusDialog({
  open,
  loading = false,
  selectedCount,
  initialValue = null,
  currentStatuses,
  onClose,
  onConfirm,
}: Props) {
  const { t } = useAppTranslation("common");

  /** statusy z configu */
  const PAYMENT_STATUS_OPTIONS = useMemo(() => {
    return Object.entries(PAYMENT_STATUS_CONFIG).map(([value, config]) => ({
      value,
      label: t(config.label),
      color: config.color,
    }));
  }, [t]);

  /** default status */
  const defaultValue = useMemo(() => {
    if (
      initialValue &&
      PAYMENT_STATUS_OPTIONS.some((o) => o.value === initialValue)
    ) {
      return initialValue;
    }

    return "__EMPTY__";
  }, [initialValue, PAYMENT_STATUS_OPTIONS]);

  const [value, setValue] = useState<string>(defaultValue);

  useEffect(() => {
    if (open) {
      setValue(defaultValue);
    }
  }, [open, defaultValue]);

  const canSubmit = !!value && selectedCount > 0 && !loading;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t("Zmień status płatności")}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* opis */}
          <Typography variant="body2" color="text.secondary">
            {t("Zmieniasz status płatności dla")} <b>{selectedCount}</b>{" "}
            {selectedCount === 1 ? t("dokumentu") : t("dokumentów")}.
          </Typography>

          {/* BONUS: pokaz aktualne statusy */}
          {currentStatuses && (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("Aktualne statusy zaznaczonych dokumentów")}
                </Typography>

                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  sx={{ mt: 0.5 }}
                >
                  {Object.entries(currentStatuses).map(([status, count]) => {
                    const config = PAYMENT_STATUS_CONFIG[status];

                    if (!config) return null;

                    return (
                      <Chip
                        key={status}
                        size="small"
                        label={`${t(config.label)} (${count})`}
                        color={config.color}
                        variant="outlined"
                      />
                    );
                  })}
                </Stack>
              </Box>

              <Divider />
            </>
          )}

          {/* select */}
          <FormControl fullWidth size="small" disabled={loading}>
            <InputLabel id="bulk-payment-status-label">
              {t("Status płatności")}
            </InputLabel>

            <Select
              labelId="bulk-payment-status-label"
              value={value}
              label={t("Status płatności")}
              onChange={(e) => setValue(e.target.value)}
            >
              {PAYMENT_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={opt.label}
                      color={opt.color}
                      variant="outlined"
                    />
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="caption" color="text.secondary">
            {t(
              "Nowy status zostanie zastosowany do wszystkich zaznaczonych dokumentów."
            )}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} variant="text">
          {t("Anuluj")}
        </Button>

        <Button
          onClick={() => onConfirm(value)}
          disabled={!canSubmit}
          variant="contained"
        >
          {t("Zastosuj")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}